import {
    css,
} from '@emotion/react'
import React, {
    ReactNode, useEffect, useRef, useState,
} from 'react'
import ReactDOM from 'react-dom'
import CircularProgress from '@mui/material/CircularProgress'

import useT from '@src/hooks/useT/useT'
import THEME from '@src/constants/Theme'

type Props = {
    className?: string
    isLoading: boolean
    children?: JSX.Element | JSX.Element[] | ReactNode
    showSpinner?: boolean
    label?: ReactNode
}

const LoadingIcon = ({
    isLoading, showSpinner, label,
}: Pick<Props, 'isLoading' | 'showSpinner' | 'label'>): JSX.Element => {
    const {
        t,
    } = useT()

    const content = label ?? t('LOADING')

    const overlay = showSpinner ? (
        <div css={styles.backdrop}>
            <div css={styles.card}>
                <CircularProgress
                    color="inherit"
                    css={styles.spinner}
                />
                <div css={styles.cardLabel}>{content}</div>
            </div>
        </div>
    ) : (
        <div css={styles.loadingIcon}>{content}</div>
    )

    const portalTarget = document.getElementById('root') ?? document.body

    return ReactDOM.createPortal(
        isLoading ? overlay : null,
        portalTarget,
    )
}

function Loading({
    className, isLoading, children, showSpinner, label,
}: Props): JSX.Element {
    const [
        loading,
        setLoading,
    ] = useState(false)
    const cancelationRef = useRef(0)

    useEffect(() => {
        if (cancelationRef.current) {
            clearTimeout(cancelationRef.current)
        }

        if (isLoading) {
            setLoading(true)
        } else {
            cancelationRef.current = window.setTimeout(() => {
                setLoading(false)
            }, 400)
        }
        return () => {
            return window.clearTimeout(cancelationRef.current)
        }
    }, [isLoading])
    return (
        <div
            css={styles.loading}
            className={className}
        >
            <div css={styles.panel(loading, showSpinner)}>{children}</div>
            <LoadingIcon
                isLoading={loading}
                showSpinner={showSpinner}
                label={label}
            />
        </div>
    )
}

const styles = {
    loading: css`
        width: 100%;
        height: 100%;
        min-height:100%;
        position: relative;
    `,

    panel: (loading: boolean, showSpinner?: boolean) => {
        return [
            css`
                width: 100%;
                min-height:100%;
                height: 100%;`,
            // The spinner variant darkens the content via its own backdrop, so
            // skip the panel fade to keep the photo visible (matches the design).
            loading && !showSpinner && css`
                    z-index: 998;
                    opacity: 0.4;
                `,
        ]
    },

    loadingIcon: css`
        position: fixed;
        left: 50%;
        top: 50vh;
        transform: translateX(-50%);
        z-index: 999;
    `,

    backdrop: css`
        position: fixed;
        inset: 0;
        z-index: 999;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(0, 0, 0, 0.2);
    `,

    card: css`
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 16px;
        padding: 24px;
        width: 280px;
        max-width: 80vw;
        border-radius: 8px;
        background: #ffffff;
        box-shadow:
            0px 16px 12px rgba(0, 0, 0, 0.14),
            0px 6px 15px rgba(0, 0, 0, 0.12),
            0px 8px 5px rgba(0, 0, 0, 0.2);
    `,

    spinner: css`
        color: ${THEME.skycell_primary_color};
    `,

    cardLabel: css`
        font-family: Roboto, sans-serif;
        font-size: 16px;
        color: #000000;
        text-align: center;
    `,
}

export default Loading
